import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react"; // Import hooks
import AllLaunches from "./container/board";
import CreateEthPump from "./container/create.tsx";
import NotFound from "./container/NotFound";
import BuyPage from "./container/BuyPage";
import { BrowserRouter as Router, Route, Switch, useHistory, useLocation } from 'react-router-dom'; // Import useLocation
import { QueryParamProvider } from 'use-query-params';
import { WagmiProvider, useAccount, useDisconnect } from 'wagmi' // Import useDisconnect
import Profile from "./container/Profile.tsx"; // <-- 1. Comment out this import
import ReferralPage from './container/Referral.tsx';
import toast from "react-hot-toast"; // Eliminamos ToastBar y Toaster que no se usan
import './index.css';
import Faq from "./container/Faq";
import { bscTestnet } from 'wagmi/chains'
import { createWeb3Modal, defaultWagmiConfig } from '@web3modal/wagmi/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { config } from './config.jsx'
import AdminPanel from './components/AdminPanel.tsx';
// Eliminamos ErrorBoundary que no se usa
// import ErrorBoundary from './components/ErrorBoundary.jsx'; // Import ErrorBoundary
// import { BASE_URL } from "./utils/constants.ts";
import VolumeBot from "./container/VolumeBot.tsx";
import ActiveStreams from "./container/ActiveStreams.tsx"; // <-- Importar nuevo componente
import StreamViewPage from "./container/StreamViewPage.tsx"; // <-- Importar la nueva página de stream
import { useWebSocket } from './hooks/useWebSocket.ts'; // Import WebSocket hook
import { fetchProfileData as fetchSenderProfileData } from './services/profileService.ts'; // Import service
// --- FIX: Correct import path for fetchNotifications ---
import { fetchNotifications, confirmPayment, rejectInvite } from './services/chatService.ts'; // Corrected import path & add confirmPayment, rejectInvite
// --- END FIX ---
import { markMessagesAsRead } from './services/chatService.ts'; // Import service
// --- FIX: Restore adapter import ---
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5'; // <-- Restore this line
// --- END FIX ---
import TopBar from './components/TopBar.jsx'; // Ensure TopBar is imported
// --- NEW: Import Payment Modal ---
import PrivateStreamPaymentModal from './container/PrivateStreamPaymentModal.tsx';
// --- END NEW ---
// --- AÑADIR: Importar PrivateRoute y funciones de autenticación ---
import PrivateRoute from './components/PrivateRoute';
import { saveWalletConnection, clearWalletConnection, isMobileDevice } from './utils/authHelpers';
// --- FIN AÑADIR ---

// --- Define Notification Type (copied from Profile.tsx) ---
// This interface definition is okay here as a comment or if your JS setup supports it,
// but it's not used directly in the useState below.
/*
interface AppNotification {
    id: string | number;
    type: 'message' | 'invite' | string;
    text: string;
    senderAddress?: string;
    data?: any;
    read: boolean;
    timestamp: number;
}
*/
// --- END ---


const projectId = '5d8afb2d4d7f105beb4757e829ac224c'

const queryClient = new QueryClient()

const currentUrl = window.location.origin;

const metadata = {
  name: 'PumpItUp',
  description: 'PumpItUp Wagmi',
  url: currentUrl,
  icons: ['https://avatars.githubusercontent.com/u/37784886']
}

const chains = [bscTestnet]

const wagmiConfig = defaultWagmiConfig({ chains, projectId, metadata })

createWeb3Modal({
  themeVariables: {
    '--w3m-accent': '#45c0be',
    '--w3m-border-radius-master': '1px'
  },
  projectId,
  metadata,
  wagmiConfig
});

// --- Main App Component ---
const AppContent = () => { // REMOVE history prop
  const { address: connectedAddress, isConnected, isConnecting } = useAccount(); // Añadir isConnecting
  const { disconnect } = useDisconnect(); // Añadir hook de desconexión
  const { lastMessage, isConnected: isWsConnected, sendMessage: sendWsMessage } = useWebSocket(connectedAddress); // Extraer isConnected y sendMessage del hook
  // --- ADD useHistory hook here ---
  const history = useHistory(); // Get history object here for general use if needed elsewhere
  
  // Ref para mantener el estado anterior de conexión y detectar desconexiones
  const wasConnectedRef = useRef(false);
  const isMobile = useRef(isMobileDevice());

  // --- Lifted Notification State ---
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // --- NEW: State for Payment Modal ---
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedRequestDetails, setSelectedRequestDetails] = useState(null);

  // --- Hack para solucionar problemas de WalletConnect en móviles ---
  useLayoutEffect(() => {
    // Limpiar datos específicos de WalletConnect para resolver problemas de conexión
    try {
      if (isMobile.current) {
        console.log('[App] Dispositivo móvil detectado, limpiando datos de WalletConnect');
        localStorage.removeItem('wc@2:core:0.3//subscription');
        
        // Si hay problemas persistentes, descomentar estas líneas:
        // localStorage.removeItem('walletconnect');
        // localStorage.removeItem('WALLETCONNECT_DEEPLINK_CHOICE');
      }
    } catch (error) {
      console.error('[App] Error al limpiar datos de WalletConnect:', error);
    }
  }, []);

  // --- AÑADIR: useEffect para persistencia de conexión ---
  useEffect(() => {
    // Cuando la dirección cambia (conecta o desconecta)
    if (connectedAddress) {
      // Guardar la dirección en localStorage
      saveWalletConnection(connectedAddress);
      wasConnectedRef.current = true;
      
      // Log para móviles
      if (isMobile.current) {
        console.log(`[App] Conexión detectada en móvil: ${connectedAddress}`);
      }
    } else if (wasConnectedRef.current && !connectedAddress) {
      // Si estaba conectado y ahora no (desconexión desde Web3Modal)
      console.log('[App] Detected disconnect from Web3Modal');
      clearWalletConnection();
      wasConnectedRef.current = false;
    }
  }, [connectedAddress]);

  // Adicionalmente, observar cambios en isConnected para detectar desconexiones directas
  useEffect(() => {
    if (!isConnected && wasConnectedRef.current) {
      console.log('[App] Detected disconnect via isConnected change');
      clearWalletConnection();
      wasConnectedRef.current = false;
    }
    
    // Manejo especial para móviles cuando la conexión cambia
    if (isMobile.current) {
      console.log(`[App Mobile] Estado de conexión: isConnecting=${isConnecting}, isConnected=${isConnected}, address=${connectedAddress || 'none'}`);
    }
  }, [isConnected, isConnecting, connectedAddress]);

  // Función para manejar la desconexión y limpiar localStorage
  const handleDisconnect = useCallback(() => {
    console.log('[App] Desconexión manual iniciada');
    
    // Primero limpiar localStorage
    clearWalletConnection();
    
    // Luego ejecutar la desconexión de wagmi
    disconnect();
    
    // Actualizar referencia
    wasConnectedRef.current = false;
    
    // En móviles, mostrar mensaje de confirmación
    if (isMobile.current) {
      toast.success('Wallet desconectada correctamente');
    }
  }, [disconnect]);

  // --- NEW: useEffect to fetch initial notifications ---
  useEffect(() => {
    const loadInitialNotifications = async () => {
      if (connectedAddress) {
        // console.log("[App] Connected address detected. Fetching initial notifications...");
        try { // Add try...catch for the fetch itself
            const initialNotifications = await fetchNotifications(connectedAddress);
            // --- ADD LOG: Log fetched notifications ---
            // console.log("[App] Fetched initial notifications raw data:", initialNotifications);
            // --- END LOG ---

            // Combine with any potentially received via WS just before this fetch completed, avoiding duplicates
            setNotifications(prev => {
                // --- ADD LOG: Log state update process ---
                // console.log("[App] Updating state with fetched notifications. Prev state:", prev);
                const existingIds = new Set(prev.map(n => n.id));
                const uniqueFetched = initialNotifications.filter(n => !existingIds.has(n.id));
                // console.log("[App] Unique fetched notifications to add:", uniqueFetched);
                // Keep existing ones first, add unique fetched ones, sort by timestamp descending, limit size
                const combined = [...prev, ...uniqueFetched];
                combined.sort((a, b) => b.timestamp - a.timestamp);
                const finalState = combined.slice(0, 50); // Limit total notifications stored
                // console.log("[App] Final notifications state after fetch:", finalState);
                // --- END LOG ---
                return finalState;
            });
            // --- FIX: Set initial unread count based ONLY on fetched data ---
            const initialUnread = initialNotifications.filter(n => !n.read).length;
            // console.log(`[App] Setting initial unread count from fetched data: ${initialUnread}`);
            setUnreadNotificationCount(initialUnread);
            // --- END FIX ---

        } catch (error) {
             console.error("[App] Error fetching initial notifications:", error);
             setNotifications([]); // Clear on error
             setUnreadNotificationCount(0);
        }
      } else {
        // console.log("[App] No connected address, clearing notifications.");
        setNotifications([]); // Clear notifications if disconnected
        setUnreadNotificationCount(0); // Reset count
      }
    };

    loadInitialNotifications();
  }, [connectedAddress]); // Run when connectedAddress changes
  // --- END NEW ---

  // --- Lifted Notification Handlers ---
  const handleNotificationClick = useCallback(async (notification) => { // Make async
    // console.log("[App] Notification clicked:", notification);
    // console.log("[App] handleNotificationClick - history object (from hook inside callback):", history);

    // Mark as read locally immediately
    setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
    // Update unread count if it was unread
    if (!notification.read) {
        setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    }


    // Action based on type
    if (notification.type === 'message' && notification.senderAddress) {
        if (history) {
            // console.log(`[App] Navigating to profile to open chat with ${notification.senderAddress}`);
            history.push('/profile', { senderAddressToOpenChat: notification.senderAddress });
        } else {
            console.error("[App] handleNotificationClick - history object is undefined. Cannot navigate.");
        }
    } else if (notification.type === 'invite' && notification.data) {
        // --- NEW: Handle Invite Click ---
        // console.log("[App] Invite notification clicked. Data:", notification.data);
        const requestData = notification.data; // Contains streamerAddress, requestId, etc.
        setSelectedRequestDetails(requestData);
        setIsPaymentModalOpen(true); // Open modal immediately

    }
    // Add other actions here...

  }, [history]); // Add history to dependencies

  const handleClearAllNotifications = useCallback(() => {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // --- NEW: Handler to mark specific notifications as read (e.g., when chat opens) ---
  const handleMarkNotificationsRead = useCallback((senderAddress) => {
      const lowerSenderAddress = senderAddress.toLowerCase();
      let markedCount = 0; // Count how many were marked read
      // console.log(`[App] Marking notifications from ${lowerSenderAddress} as read.`);
      setNotifications(prevNotifications =>
          prevNotifications.map(n => {
              if (n.type === 'message' && n.senderAddress === lowerSenderAddress && !n.read) {
                  markedCount++; // Increment count if marking this one
                  return { ...n, read: true };
              }
              return n;
          })
      );
      // --- FIX: Decrement unread count by the number of messages actually marked read ---
      if (markedCount > 0) {
          setUnreadNotificationCount(prev => Math.max(0, prev - markedCount));
      }
      // --- END FIX ---
      // Also mark as read in backend (fire and forget)
      if (connectedAddress) {
          markMessagesAsRead(connectedAddress, lowerSenderAddress)
              .catch(err => console.error("[App] Error marking messages as read in backend:", err));
      }
  }, [connectedAddress]);
  // --- END NEW ---

  // --- NEW: Handlers for Payment Modal ---
  const handlePaymentSuccess = useCallback(async (txHash, requestId) => {
      // console.log(`[App] handlePaymentSuccess called. Tx: ${txHash}, RequestID: ${requestId}`);
      try {
          const result = await confirmPayment(requestId, txHash);
          if (result.success) {
              toast.success(result.message || 'Payment confirmed! Access granted.');
              // Remove the notification
              setNotifications(prev => prev.filter(n => n.id !== requestId));
              setIsPaymentModalOpen(false); // Close modal
              setSelectedRequestDetails(null);
              // Optionally navigate to the stream page or update UI
          } else {
              throw new Error(result.message || 'Backend failed to confirm payment.');
          }
      } catch (error) {
          console.error("[App] Error confirming payment with backend:", error);
          toast.error(`Payment confirmation failed: ${error.message}`);
          // Keep modal open? Or close? Decide based on UX preference.
          // For now, we keep it open so user sees the error state within the modal.
          // The modal's internal state should reflect the backend processing failure.
          // Re-throwing might be needed if the modal handles its own loading state reset on error.
          // throw error; // Re-throw if modal needs to handle this
      }
      // Note: Modal's internal isProcessingBackend state should be reset by the modal itself
  }, []);

  const handleRejectSuccess = useCallback(async (requestId) => {
      // console.log(`[App] handleRejectSuccess called. RequestID: ${requestId}`);
      try {
          const result = await rejectInvite(requestId);
           if (result.success) {
               toast.success(result.message || 'Invitation rejected.');
               // Remove the notification
               setNotifications(prev => prev.filter(n => n.id !== requestId));
               setIsPaymentModalOpen(false); // Close modal
               setSelectedRequestDetails(null);
           } else {
               throw new Error(result.message || 'Backend failed to reject invitation.');
           }
      } catch (error) {
          console.error("[App] Error rejecting invitation with backend:", error);
          toast.error(`Rejection failed: ${error.message}`);
          // Keep modal open? Or close?
          // Re-throwing might be needed if the modal handles its own loading state reset on error.
          // throw error; // Re-throw if modal needs to handle this
      }
      // Note: Modal's internal isProcessingBackend state should be reset by the modal itself
  }, []);

  const handleClosePaymentModal = useCallback(() => {
      setIsPaymentModalOpen(false);
      setSelectedRequestDetails(null);
  }, []);
  // --- END NEW ---

  // Prep global props to pass down to all routes
  // Use memo to prevent unnecessary rerenders of child components
  const globalProps = useMemo(() => {
    return {
      notifications,
      unreadNotificationCount,
      onNotificationClick: handleNotificationClick,
      onClearAllNotifications: handleClearAllNotifications,
      onMarkNotificationsRead: handleMarkNotificationsRead,
      onDisconnect: handleDisconnect, // Añadir handler de desconexión a props globales
      lastMessage, // Añadir mensaje WebSocket
      isWsConnected, // Añadir estado de conexión WebSocket
      sendWsMessage, // Añadir función para enviar mensajes WebSocket
      isMobile: isMobile.current, // Añadir indicador de dispositivo móvil
    };
  }, [notifications, unreadNotificationCount, handleNotificationClick, handleClearAllNotifications, handleMarkNotificationsRead, handleDisconnect, lastMessage, isWsConnected, sendWsMessage]);

  // --- ADDED: Log globalProps before rendering routes ---
  // console.log("[App] Rendering AppContent. globalProps:", globalProps);
  // --- END ADDED ---

  // --- WebSocket Message Handling --- // <<<--- MOVED THE useEffect BLOCK HERE ---<<<
  useEffect(() => {
    // --- ADD LOG: Log ALL received messages ---
    // console.log('[App WebSocket] Received message:', lastMessage);
    // --- END LOG ---

    if (lastMessage && connectedAddress) {
        const currentUserAddrLower = connectedAddress.toLowerCase();
        let newNotification = null;

        // --- ADD LOG: Processing message type ---
        // console.log(`[App WebSocket] Processing message type: ${lastMessage.type}`);
        // --- END LOG ---

        switch (lastMessage.type) {
            case 'NEW_MESSAGE':
                const receivedMessage = lastMessage.payload;
                const senderAddress = receivedMessage.senderAddress.toLowerCase();

                // --- ADD LOG: Processing NEW_MESSAGE ---
                // console.log(`[App WebSocket] NEW_MESSAGE from ${senderAddress}. Current user: ${currentUserAddrLower}`);
                // --- END LOG ---

                if (senderAddress !== currentUserAddrLower) {
                    // Check if chat is open (this logic might need refinement if chat state is complex)
                    // For now, assume chat state isn't easily accessible here, always notify if not self
                    // console.log(`[App WebSocket] Message is from another user (${senderAddress}).`);

                    // --- FIX: Increment count FIRST, then handle notification/toast ---
                    setUnreadNotificationCount(prev => {
                        const newCount = prev + 1;
                        // console.log('[App WebSocket] Updated unreadNotificationCount state (message):', newCount);
                        return newCount;
                    });
                    // --- END FIX ---

                    // Fetch sender profile for better notification text
                    fetchSenderProfileData(senderAddress)
                        .then(senderProfile => {
                            const senderName = senderProfile?.username || `${senderAddress.substring(0, 6)}...`;
                            const notificationText = `New message from ${senderName}`;

                            // --- ADD LOG: Creating message notification ---
                            // console.log(`[App WebSocket] Creating notification: ${notificationText}`);
                            // --- END LOG ---

                            newNotification = {
                                id: receivedMessage._id || `${senderAddress}-${receivedMessage.timestamp}`,
                                type: 'message',
                                text: notificationText,
                                senderAddress: senderAddress,
                                read: false, // Always false for new message notification
                                timestamp: new Date(receivedMessage.timestamp).getTime(),
                                // --- ADD: Include full message payload in data ---
                                data: receivedMessage
                                // --- END ADD ---
                            };
                            setNotifications(prev => {
                                const newState = [newNotification, ...prev.filter(n => n.id !== newNotification.id).slice(0, 19)];
                                // console.log('[App WebSocket] Updated notifications state (message):', newState);
                                return newState;
                            });
                            // --- REMOVED: Count increment moved above ---
                            // setUnreadNotificationCount(prev => prev + 1);
                            // --- END REMOVED ---
                            toast(notificationText, {
                                // Optional: Add onClick to navigate/open chat later
                            });
                        })
                        .catch(err => {
                            // Fallback if profile fetch fails
                            const senderName = `${senderAddress.substring(0, 6)}...`;
                            const notificationText = `New message from ${senderName}`;
                            console.error("[App WebSocket] Failed to fetch sender profile for notification:", err);
                            newNotification = {
                                id: receivedMessage._id || `${senderAddress}-${receivedMessage.timestamp}`,
                                type: 'message',
                                text: notificationText,
                                senderAddress: senderAddress,
                                read: false,
                                timestamp: new Date(receivedMessage.timestamp).getTime(),
                                // --- ADD: Include full message payload in data ---
                                data: receivedMessage
                                // --- END ADD ---
                            };
                            setNotifications(prev => [newNotification, ...prev.filter(n => n.id !== newNotification.id).slice(0, 19)]);
                            // --- REMOVED: Count increment moved above ---
                            // setUnreadNotificationCount(prev => prev + 1);
                            // --- END REMOVED ---
                            toast(notificationText);
                        });
                } else {
                    // console.log(`[App WebSocket] Ignoring message from self (${senderAddress}).`);
                }
                break;

            case 'PRIVATE_STREAM_REQUEST': // User receives an invite
                const invitePayload = lastMessage.payload;
                // console.log('[App WebSocket] Received PRIVATE_STREAM_REQUEST:', invitePayload);

                // --- FIX: Increment count FIRST ---
                setUnreadNotificationCount(prev => {
                    const newCount = prev + 1;
                    // console.log('[App WebSocket] Updated unreadNotificationCount state (invite):', newCount);
                    return newCount;
                });
                // --- END FIX ---

                // --- SIMPLIFIED LOGIC: Use data directly from payload ---
                const streamerName = invitePayload.streamerUsername || `${invitePayload.streamerAddress.substring(0, 6)}...`;
                const notificationText = `Stream invite from ${streamerName} (${invitePayload.entryPriceBNB} BNB)`;

                // console.log(`[App WebSocket] Creating invite notification: ${notificationText}`);

                newNotification = {
                    id: invitePayload.requestId || Date.now(),
                    type: 'invite',
                    text: notificationText,
                    // data now contains username and picture directly from backend payload
                    data: invitePayload,
                    read: false, // Always false for new invite notification
                    // Use timestamp from payload if available, otherwise fallback
                    timestamp: invitePayload.timestamp ? new Date(invitePayload.timestamp).getTime() : Date.now(),
                };

                setNotifications(prev => {
                    const newState = [newNotification, ...prev.filter(n => n.id !== newNotification.id).slice(0, 19)];
                    // console.log('[App WebSocket] Updated notifications state (invite):', newState);
                    return newState;
                });
                // --- REMOVED: Count increment moved above ---
                // setUnreadNotificationCount(prev => prev + 1);
                // --- END REMOVED ---

                toast(notificationText, {
                    // onClick: () => handleInviteClick(invitePayload) // You can still add onClick if needed
                });
                // --- END SIMPLIFIED LOGIC ---
                break;

            case 'PONG':
                 // console.log('[App WebSocket] Received PONG from server.');
                 break;

            default:
                console.warn(`[App WebSocket] Received unknown message type: ${lastMessage.type}`);
                break;
        }
    }
}, [lastMessage, connectedAddress]); // Dependencies
// --- END WebSocket Message Handling ---

  return (
    <QueryParamProvider adapter={ReactRouter5Adapter}>
      <div className={`min-h-screen font-inter text-white bg-[#010101] md:pl-0 md:pt-0 ${isMobile.current ? 'mobile-view' : ''}`}>
        <TopBar
          notifications={notifications}
          unreadNotificationCount={unreadNotificationCount}
          onNotificationClick={handleNotificationClick}
          onClearAllNotifications={handleClearAllNotifications}
          onDisconnect={handleDisconnect}
          isMobile={isMobile.current}
        />
        
        <Switch>
          {/* Pass globalProps to each route component */}
          <Route exact path="/">
            <AllLaunches {...globalProps} />
          </Route>
          <Route exact path="/dashboard">
            <AllLaunches {...globalProps} />
          </Route>
          {/* Rutas protegidas que requieren conexión de wallet */}
          <PrivateRoute exact path="/create" component={CreateEthPump} {...globalProps} />
          <PrivateRoute exact path="/Buy" component={BuyPage} {...globalProps} />
          <PrivateRoute exact path="/profile" component={Profile} {...globalProps} />
          <Route exact path="/intel">
            <Faq {...globalProps} />
          </Route>
          <PrivateRoute exact path="/referral" component={ReferralPage} {...globalProps} />
          <Route exact path="/streams">
            <ActiveStreams {...globalProps} />
          </Route>
          <PrivateRoute exact path="/stream/:tokenAddress" component={StreamViewPage} {...globalProps} />
          <PrivateRoute exact path="/admin" component={AdminPanel} {...globalProps} />
          <PrivateRoute exact path="/volume-bot" component={VolumeBot} {...globalProps} />
          <Route exact path="/NotFound">
            <NotFound />
          </Route>
        </Switch>
      </div>

      {/* --- NEW: Render Payment Modal --- */}
      <PrivateStreamPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={handleClosePaymentModal}
          requestDetails={selectedRequestDetails}
          onPaymentSuccess={handlePaymentSuccess}
          onRejectSuccess={handleRejectSuccess}
          isMobile={isMobile.current}
      />
      {/* --- END NEW --- */}

    </QueryParamProvider> 
  );
};
// --- End Main App Component ---


const App = () => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AppContent />
        </Router>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default App;